import React, { useState, useEffect } from "react";
import "./Signup.css";
import { FaEye, FaEyeSlash, FaInfoCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/ExamWarriorLogo.svg"; // Make sure to have your logo in the src folder

const Signup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    parent: { firstName: "", lastName: "", phone: "", email: "" },
    children: [],
    currentChild: {
      firstName: "",
      lastName: "",
      grade: "",
      school: "",
      username: "",
      password: "",
      isOC: false,
      isSelective: false
    },
    terms: false
  });

  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [is2FASent, setIs2FASent] = useState(false);
  const [otp, setOtp] = useState("");
  const [childCount, setChildCount] = useState(1);
  const [emailforverifyotp, setUserEmailForOTP] = useState("");
  const [shown, setShown] = useState(false);
  const [formMessage, setFormMessage] = useState({ type: "", text: "" });

  // Function to debounce username check
  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  };

  const debouncedUsername = useDebounce(formData.currentChild.username, 500);

  // Function to check username availability
  const checkUsername = async (username) => {
    if (!username) {
      setUsernameAvailable(null);
      return;
    }

    setCheckingUsername(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/check-username?username=${encodeURIComponent(username)}`
      );
      const data = await response.json();
      setUsernameAvailable(data.available);
    } catch (error) {
      console.error("Error checking username:", error);
      setUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  };

  useEffect(() => {
    checkUsername(debouncedUsername);
  }, [debouncedUsername]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      if (name === "terms") {
        setFormData((prevData) => ({
          ...prevData,
          terms: checked
        }));
      } else if (name.startsWith("child_")) {
        const childField = name.replace("child_", "");
        setFormData((prevData) => ({
          ...prevData,
          currentChild: {
            ...prevData.currentChild,
            [childField]: checked
          }
        }));
      }
    } else {
      if (name.startsWith("parent_")) {
        const parentField = name.replace("parent_", "");
        setFormData((prevData) => ({
          ...prevData,
          parent: { ...prevData.parent, [parentField]: value }
        }));
      } else if (name.startsWith("child_")) {
        const childField = name.replace("child_", "");
        setFormData((prevData) => ({
          ...prevData,
          currentChild: { ...prevData.currentChild, [childField]: value }
        }));
      }
    }
  };

  const handleAddChild = () => {
    const { firstName, lastName, grade, school, username, password, isOC, isSelective } =
      formData.currentChild;

    if (!firstName || !lastName || !grade || !username || !password) {
      alert("Please fill all required child fields");
      return;
    }

    setFormData((prevData) => {
      const newChild = {
        firstName,
        lastName,
        grade,
        school,
        username,
        password,
        isOC,
        isSelective
      };

      return {
        ...prevData,
        children: [...prevData.children, newChild],
        currentChild: {
          firstName: "",
          lastName: "",
          grade: "",
          school: "",
          username: "",
          password: "",
          isOC: false,
          isSelective: false
        }
      };
    });
    setChildCount(childCount + 1);
  };

  const handleRemoveChild = (index) => {
    const updatedChildren = formData.children.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      children: updatedChildren
    });
    setChildCount(childCount - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.children.length === 0) {
      setFormMessage({ type: "error", text: "Please add at least one child." });
      return;
    }

    if (!formData.terms) {
      setFormMessage({ type: "error", text: "Please agree to the terms and services." });
      return;
    }

    const { firstName, lastName, phone, email } = formData.parent;

    if (!firstName || !lastName || !phone || !email) {
      setFormMessage({ type: "error", text: "Please fill in all required parent fields." });
      return;
    }

    if (usernameAvailable === false) {
      setFormMessage({ type: "error", text: "Username is already taken. Please choose another one." });
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/signup`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            parent: formData.parent,
            children: formData.children,
            terms: formData.terms
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        setFormMessage({ type: "success", text: "Signup successful! Redirecting..." });
        setTimeout(() => navigate("/login"), 2000);
      } else {
        // Check for duplicate email error
        if (response.status === 409 ||
          (data.message && data.message.toLowerCase().includes("email already exists")) ||
          (data.message && data.message.toLowerCase().includes("duplicate")) ||
          (data.error && data.error.toLowerCase().includes("duplicate"))) {
          setFormMessage({
            type: "error",
            text: "This email address is already registered. Please use a different email or try logging in."
          });
        }
        // Check for duplicate username error
        else if (data.message && data.message.toLowerCase().includes("username already exists")) {
          setFormMessage({
            type: "error",
            text: "This username is already taken. Please choose another username."
          });
        }
        // Generic error message
        else {
          setFormMessage({ type: "error", text: data.message || "Signup failed. Please try again." });
        }
      }
    } catch (error) {
      console.error("Error:", error);

      // Check if it's a duplicate entry error from the database
      if (error.message && (
        error.message.toLowerCase().includes("duplicate") ||
        error.message.toLowerCase().includes("already exists")
      )) {
        setFormMessage({
          type: "error",
          text: "This email address is already registered. Please use a different email or try logging in."
        });
      } else {
        setFormMessage({
          type: "error",
          text: "An error occurred while signing up. Please try again."
        });
      }
    }
  };
  useEffect(() => {
    if (formMessage.text) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [formMessage.text]);

  return (

    <div className="signup-container">
      {formMessage.text && (
        <div className={`form-message ${formMessage.type}`}>
          {formMessage.text}
        </div>
      )}

      <div className="signup-header">
        {/* <img src={logo} alt="Site Logo" className="site-logo" /> */}
        <h2>Create Your Account</h2>
        <p>Join our community and get started with your educational journey</p>
      </div>

      <div className="signup-content">
        {/* Parent Section - Left Side */}
        <div className="parent-section">
          <div className="form-card">
            <div className="section-header">
              <div className="icon-circle teal-bg">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              <h3>Parent Information</h3>
            </div>

            <div className="input-group">
              <div className="input-field-wrapper">
                <label>First Name</label>
                <input
                  type="text"
                  name="parent_firstName"
                  placeholder="Enter first name"
                  value={formData.parent.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="input-field-wrapper">
                <label>Last Name</label>
                <input
                  type="text"
                  name="parent_lastName"
                  placeholder="Enter last name"
                  value={formData.parent.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="input-field-wrapper">
              <label>Phone Number</label>
              <input
                type="text"
                name="parent_phone"
                placeholder="Enter phone number"
                value={formData.parent.phone}
                onChange={handleChange}
                required
              />
            </div>

            <div className="input-field-wrapper">
              <label>Email Address</label>
              <input
                type="email"
                name="parent_email"
                placeholder="Enter email address"
                value={formData.parent.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>
        </div>

        {/* Children Section - Right Side */}
        <div className="children-section">
          <div className="form-card">
            <div className="section-header">
              <div className="icon-circle purple-bg">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <h3>Child {childCount} Information</h3>
            </div>

            <div className="input-group">
              <div className="input-field-wrapper">
                <label>First Name</label>
                <input
                  type="text"
                  name="child_firstName"
                  placeholder="Enter first name"
                  value={formData.currentChild.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="input-field-wrapper">
                <label>Last Name</label>
                <input
                  type="text"
                  name="child_lastName"
                  placeholder="Enter last name"
                  value={formData.currentChild.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="input-group">
              <div className="input-field-wrapper">
                <label>Grade (Year)</label>
                <input
                  type="text"
                  name="child_grade"
                  placeholder="Enter grade"
                  value={formData.currentChild.grade}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="input-field-wrapper">
                <label>School (Optional)</label>
                <input
                  type="text"
                  name="child_school"
                  placeholder="Enter school name"
                  value={formData.currentChild.school}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* OC and Selective Checkboxes */}
            <div className="checkbox-group">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  name="child_isOC"
                  checked={formData.currentChild.isOC}
                  onChange={handleChange}
                  disabled={![3, 4].includes(parseInt(formData.currentChild.grade))}
                />
                <span className="checkmark"></span>
                <span className="checkbox-label">Opportunity Class (OC)</span>
              </label>

              <label className="checkbox-container">
                <input
                  type="checkbox"
                  name="child_isSelective"
                  checked={formData.currentChild.isSelective}
                  onChange={handleChange}
                  disabled={![5, 6].includes(parseInt(formData.currentChild.grade))}
                />
                <span className="checkmark"></span>
                <span className="checkbox-label">Selective</span>
              </label>
            </div>

            <div className="input-group">

              <div className="input-field-wrapper">
                <label>Username</label>
                <div className="username-wrapper">
                  <input
                    type="text"
                    name="child_username"
                    placeholder="Choose a username"
                    value={formData.currentChild.username}
                    onChange={handleChange}
                    required
                  />
                  {checkingUsername && (
                    <span className="username-status">Checking...</span>
                  )}
                  {usernameAvailable === true && (
                    <span className="username-status available">✓ Available</span>
                  )}
                  {usernameAvailable === false && (
                    <span className="username-status taken">✗ Taken</span>
                  )}
                </div>
              </div>
              <div className="input-field-wrapper">
                <label>Password</label>
                <div className="custom-password-wrapper">
                  <input
                    type={shown ? "text" : "password"}
                    name="child_password"
                    placeholder="Create a password"
                    value={formData.currentChild.password}
                    onChange={handleChange}
                    required
                  />
                  <span
                    onClick={() => setShown(!shown)}
                    className="custom-password-toggle-icon"
                  >
                    {shown ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                  </span>
                </div>
              </div>
              <div className="login-info-note" style={{ display: "flex", alignItems: "center", marginBottom: "10px", color: "#555" }}>
                <span style={{ marginRight: "6px", color: "#00b4b4" }}>
                  <FaInfoCircle />
                </span>
                <span style={{ fontStyle: "italic", color: "#555" }}>This username and password will be used to log in.</span>
              </div>
            </div>

            <button
              type="button"
              className="add-child-btn"
              onClick={handleAddChild}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add This Child
            </button>
          </div>

          {/* Display Added Children */}
          {formData.children.length > 0 && (
            <div className="form-card added-children-section">
              <div className="section-header">
                <div className="icon-circle blue-bg">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <h3>Your Children ({formData.children.length})</h3>
              </div>

              <div className="children-list">
                {formData.children.map((child, index) => (
                  <div key={index} className="child-item">
                    <div className="child-info">
                      <div className="child-avatar">{child.firstName.charAt(0)}{child.lastName.charAt(0)}</div>
                      <div>
                        <div className="child-name">{child.firstName} {child.lastName}</div>
                        <div className="child-details">
                          Grade {child.grade} • {child.school || "School not specified"}
                          {child.isOC && " • OC"}
                          {child.isSelective && " • Selective"}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="remove-child-btn"
                      onClick={() => handleRemoveChild(index)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Terms and Submit */}
          <div className="form-card terms-section">
            <div className="terms">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  id="terms"
                  name="terms"
                  checked={formData.terms}
                  onChange={handleChange}
                  required
                />
                <span className="checkmark"></span>
                <span className="checkbox-label">
                  I agree to the <a href="/terms&condition">terms and services</a> and have read
                  the <a href="/privacy-policy">privacy policy</a>.
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="submit-btn"
              onClick={handleSubmit}
              disabled={checkingUsername}
            >
              Complete Sign Up
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;